import "./style.css";
import type { ReactionId } from "@meksiko/shared-types";
import { preloadCardImages } from "./cards/assets.js";
import { getLanguage, LANGUAGES, type LanguageCode } from "./i18n/index.js";
import { updateRotateOverlayText } from "./i18n/rotateOverlay.js";
import { isFacebookEmbed } from "./platform/index.js";
import { useAppStore } from "./store/appStore.js";
import {
  getSelectedDiscard,
  mountFacebookOverlays,
  renderApp,
  resetDiscardSelection,
  tickTurnTimer,
  tickWaitingTimer,
  toggleDiscardSelection,
} from "./ui/render.js";

// The Facebook Instant Games iframe is narrower than a typical desktop
// browser tab, which made the lobby's fixed-position leaderboard preview
// panel overlap the centered play button (GAME_SPEC.md §14 layout) — see
// referent/screen.png. Rather than shrinking that panel everywhere (the
// larger fonts across the whole app are a deliberate accessibility choice,
// see CLAUDE.md), scope the shrink to the Facebook embed only via this class.
if (isFacebookEmbed()) document.documentElement.classList.add("platform-facebook");

function render(): void {
  const state = useAppStore.getState();
  renderApp(state);
  mountFacebookOverlays(state);
}

useAppStore.subscribe(render);
render();
preloadCardImages();

// #rotate-overlay and <html lang> are outside the normal render() cycle (see
// i18n/rotateOverlay.ts) — set their initial value once here at boot, from
// whatever language bootstrap()/the store already resolved from localStorage.
document.documentElement.lang = LANGUAGES.find((l) => l.code === getLanguage())?.bcp47Tag ?? getLanguage();
updateRotateOverlayText();

// Live countdown (GAME_SPEC.md §10): the server only pushes state on each
// validated action, so tick the countdown text/bar locally in between —
// but via a targeted DOM update (tickTurnTimer), not a full render(). A full
// re-render tears down and recreates every element on screen, which would
// restart any CSS :hover transition (card lift, button lift) each tick,
// making whatever the mouse was over visibly judder/bounce 4x a second.
setInterval(() => {
  const state = useAppStore.getState();
  if (state.matchView) tickTurnTimer(state.matchView);
  if (state.waiting) tickWaitingTimer(state.waiting);
}, 250);

// Best-effort landscape lock for phones/tablets (GAME_SPEC.md mobile pass).
// Real orientation locking only works while the page is fullscreen, and
// browsers only grant fullscreen from inside a trusted, synchronous gesture
// handler — so this piggybacks on every tap rather than running on load.
// It's pure progressive enhancement: Android Chrome actually locks to
// landscape; iOS Safari supports neither API and both calls just reject
// silently, leaving the CSS-only rotate-overlay (see index.html/style.css)
// as the fallback there.
//
// Deliberately re-checked on EVERY click rather than gated behind a
// one-shot "already tried" flag: backgrounding the tab (e.g. switching apps
// on Android, or the OS suspending Chrome) silently drops fullscreen, and
// there is no reliable event to catch that moment to re-arm a one-shot
// flag. Checking `document.fullscreenElement` fresh each click means the
// very next tap after returning from the background re-requests fullscreen
// instead of leaving the user permanently stuck outside it.
function tryEnterFullscreenLandscape(): void {
  if (document.fullscreenElement) return; // already fullscreen, nothing to do
  if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return; // desktop mouse users don't want this

  const root = document.documentElement as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> };
  const requestFullscreen = root.requestFullscreen?.bind(root) ?? root.webkitRequestFullscreen?.bind(root);
  const lockLandscape = () =>
    (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.("landscape")?.catch(() => {});

  if (requestFullscreen) {
    requestFullscreen()
      .then(lockLandscape)
      .catch(() => {});
  } else {
    lockLandscape();
  }
}

document.addEventListener("click", (event) => {
  tryEnterFullscreenLandscape();
  const store = useAppStore.getState();
  // GAME_SPEC.md §10: any click while the AI is standing in for a timed-out
  // (but still connected) player hands control back to them — deliberately
  // not scoped to a specific button, since the point is "do anything at all".
  const view = store.matchView;
  const mySeat = view ? view.seats[view.mySeat] : undefined;
  if (mySeat?.isBot && mySeat.connected) {
    store.connection?.sendResumeControl();
  }

  const target = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!target) return;
  const action = target.dataset.action;

  switch (action) {
    case "login-facebook":
      store.login("facebook");
      break;
    case "login-google":
      store.login("google-oauth2");
      break;
    case "login-email":
      store.login();
      break;
    case "join-queue":
      void store.joinQueue();
      break;
    case "share-invite":
      store.shareInvite();
      break;
    case "close-insufficient-funds":
      store.closeInsufficientFundsModal();
      break;
    case "watch-ad":
      void store.watchAd();
      break;
    case "open-leaderboard":
      store.openLeaderboardScreen();
      break;
    case "leaderboard-tab-season":
      store.setLeaderboardTab("season");
      break;
    case "leaderboard-tab-global":
      store.setLeaderboardTab("global");
      break;
    case "open-profile":
      void store.openProfileScreen();
      break;
    case "nav-lobby":
      store.backToLobby();
      break;
    case "cancel-queue":
    case "leave-match":
      store.leaveMatch();
      break;
    case "bid":
      store.sendGameAction({ type: "bid", value: Number(target.dataset.value) as never });
      break;
    case "pass":
      store.sendGameAction({ type: "pass" });
      break;
    case "choose-trump":
      store.sendGameAction({ type: "chooseTrump", suit: target.dataset.suit as never });
      break;
    case "toggle-discard": {
      toggleDiscardSelection(target.dataset.card!);
      // No confirm popup (GAME_SPEC.md §6) — picking the 2nd card submits
      // the discard immediately.
      const [a, b] = getSelectedDiscard();
      if (a && b) {
        store.sendGameAction({ type: "discard", cards: [a, b] });
        resetDiscardSelection();
      }
      render();
      break;
    }
    case "play-card":
      store.sendGameAction({ type: "playCard", card: target.dataset.card! });
      break;
    case "toggle-reactions":
      store.toggleReactionMenu();
      break;
    case "close-reactions":
      store.closeReactionMenu();
      break;
    case "send-reaction":
      store.sendReaction(target.dataset.reaction as ReactionId);
      break;
    case "noop":
      break;
    case "open-rules":
      store.openRules();
      break;
    case "close-rules":
      store.closeRules();
      break;
    case "open-settings":
      store.openSettings();
      break;
    case "close-settings":
      store.closeSettings();
      break;
    case "logout":
      void store.logout();
      break;
    case "open-delete-account":
      store.openDeleteAccount();
      break;
    case "close-delete-account":
      store.closeDeleteAccount();
      break;
    case "confirm-delete-account":
      void store.deleteAccount();
      break;
    case "toggle-mute":
      store.toggleMute();
      break;
    case "save-display-name": {
      const input = document.getElementById("display-name-input") as HTMLInputElement | null;
      if (input) void store.updateDisplayName(input.value);
      break;
    }
    case "back-to-lobby":
      store.dismissMatchEnd();
      break;
    case "reload":
      location.reload();
      break;
    case "toggle-language-menu":
      store.toggleLanguageMenu();
      break;
    case "close-language-menu":
      store.closeLanguageMenu();
      break;
    case "select-language":
      store.setLanguage(target.dataset.lang as LanguageCode);
      break;
  }
});

void useAppStore.getState().bootstrap();
